/**
 * MarketingPage — Digital Marketing Operations Phase 1
 * ────────────────────────────────────────────────────
 * Three tabs:
 *   • Agreement  — services purchased + monthly deliverables per client
 *   • Roster     — social media planner (month calendar + inline editor + live counters)
 *   • SEO        — off-page tracker with bulk entry + manager verify
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SearchableSelect from '../components/SearchableSelect';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

const PLATFORMS = [
  { k: 'facebook', label: 'Facebook' },
  { k: 'instagram', label: 'Instagram' },
  { k: 'linkedin', label: 'LinkedIn' },
  { k: 'youtube', label: 'YouTube' },
  { k: 'twitter', label: 'X (Twitter)' },
  { k: 'google_business', label: 'Google Business' },
];
const CONTENT_TYPES = ['poster', 'video', 'reel', 'story', 'carousel', 'text', 'live'];
const POST_STATUSES = [
  'planned', 'design_pending', 'content_pending', 'ready_for_approval',
  'client_approval_pending', 'approved', 'scheduled', 'posted', 'rejected',
];
const POST_STATUS_COLOR = {
  planned: 'bg-slate-100 text-slate-700',
  design_pending: 'bg-amber-100 text-amber-800',
  content_pending: 'bg-orange-100 text-orange-800',
  ready_for_approval: 'bg-blue-100 text-blue-800',
  client_approval_pending: 'bg-purple-100 text-purple-800',
  approved: 'bg-emerald-100 text-emerald-800',
  scheduled: 'bg-teal-100 text-teal-800',
  posted: 'bg-emerald-200 text-emerald-900 font-bold',
  rejected: 'bg-rose-100 text-rose-800',
};
const SEO_TYPES = [
  'backlink', 'directory_submission', 'profile_creation', 'blog_comment',
  'social_bookmarking', 'guest_post', 'classified', 'web_2_0', 'citation',
  'forum_posting', 'article_submission', 'press_release', 'image_submission',
  'video_submission', 'pdf_submission', 'ppt_submission',
];
const SEO_STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  submitted: 'bg-blue-100 text-blue-800',
  verified: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  needs_correction: 'bg-orange-100 text-orange-800',
};

const clientName = (c) => (c.company_name || `${c.first_name || ''} ${c.last_name || ''}`).trim() || '(unnamed)';
const toIsoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function MarketingPage() {
  const [tab, setTab] = useState('roster');
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/clients`, opts).then((r) => setClients(r.data || [])).catch(() => {});
    axios.get(`${API}/api/users`, opts).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const clientLabel = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, clientName(c)])), [clients]);
  const userLabel = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name || u.email])), [users]);

  const TABS = [
    { k: 'roster', label: 'Social Roster' },
    { k: 'agreement', label: 'Marketing Agreement' },
    { k: 'seo', label: 'SEO Off-Page' },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="marketing-page">
      <div>
        <h1 className="text-2xl font-bold text-emerald-800">Digital Marketing Operations</h1>
        <p className="text-sm text-gray-600">Plan · Deliver · Track — social media roster, SEO off-page, agreements.</p>
      </div>
      <div className="flex gap-1 flex-wrap border-b">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${tab === t.k ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`marketing-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'roster' && <RosterTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'agreement' && <AgreementTab clients={clients} clientLabel={clientLabel} />}
      {tab === 'seo' && <SeoTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
    </div>
  );
}

// ═════════════════════ Marketing Agreement Tab ═════════════════════

const SERVICE_OPTIONS = [
  { k: 'social_media', label: 'Social Media Management' },
  { k: 'seo', label: 'SEO' },
  { k: 'google_ads', label: 'Google Ads' },
  { k: 'meta_ads', label: 'Meta Ads' },
  { k: 'website_maintenance', label: 'Website Maintenance' },
  { k: 'content_writing', label: 'Content Writing' },
  { k: 'email_marketing', label: 'Email Marketing' },
];

function AgreementTab({ clients, clientLabel }) {
  const [activeClient, setActiveClient] = useState('');
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);

  const emptyAgreement = (cid) => ({
    client_id: cid, services: [], social_platforms: [],
    monthly_deliverables: {}, target_keywords: [], monthly_backlink_target: 0,
    notes: '', active: true,
  });

  const load = useCallback(async (cid) => {
    if (!cid) { setAgreement(null); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/dmo/agreements/${cid}`, opts);
      setAgreement(r.data || emptyAgreement(cid));
    } catch (e) { setAgreement(emptyAgreement(cid)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(activeClient); }, [activeClient, load]);

  const save = async () => {
    if (!agreement) return;
    try {
      await axios.post(`${API}/api/dmo/agreements`, agreement, opts);
      toast.success('Marketing agreement saved');
      load(activeClient);
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const toggleService = (k) => {
    setAgreement((a) => ({
      ...a,
      services: a.services.includes(k) ? a.services.filter((x) => x !== k) : [...a.services, k],
    }));
  };
  const togglePlatform = (k) => {
    setAgreement((a) => ({
      ...a,
      social_platforms: a.social_platforms.includes(k) ? a.social_platforms.filter((x) => x !== k) : [...a.social_platforms, k],
    }));
  };
  const setDeliverable = (platform, ctype, qty) => {
    setAgreement((a) => {
      const md = { ...(a.monthly_deliverables || {}) };
      md[platform] = { ...(md[platform] || {}) };
      const q = parseInt(qty, 10) || 0;
      if (q > 0) md[platform][ctype] = q; else delete md[platform][ctype];
      if (Object.keys(md[platform]).length === 0) delete md[platform];
      return { ...a, monthly_deliverables: md };
    });
  };

  const activeClients = clients.filter((c) => !!c.id);

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <Label className="text-xs font-semibold">Client:</Label>
        <div className="min-w-[260px]">
          <SearchableSelect
            testId="agreement-client"
            value={activeClient}
            onChange={setActiveClient}
            placeholder="Pick a client (type to search)…"
            options={[
              { value: '', label: '— Pick a client —' },
              ...activeClients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
            ]}
          />
        </div>
        {activeClient && !loading && (
          <Button size="sm" onClick={save} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-agreement">Save Agreement</Button>
        )}
      </Card>

      {!activeClient ? (
        <Card className="p-8 text-center text-sm text-gray-400">Pick a client to view or set up their marketing agreement.</Card>
      ) : loading || !agreement ? (
        <Card className="p-6 text-center text-sm text-gray-400">Loading…</Card>
      ) : (
        <>
          <Card className="p-4">
            <h3 className="font-semibold mb-2 text-emerald-800">Services Purchased</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <label key={s.k} className={`flex items-center gap-2 text-sm cursor-pointer border rounded p-2 ${agreement.services.includes(s.k) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                  <input type="checkbox" checked={agreement.services.includes(s.k)} onChange={() => toggleService(s.k)} data-testid={`svc-${s.k}`} />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>
          </Card>

          {agreement.services.includes('social_media') && (
            <Card className="p-4">
              <h3 className="font-semibold mb-2 text-emerald-800">Social Platforms</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {PLATFORMS.map((p) => (
                  <label key={p.k} className={`flex items-center gap-2 text-sm cursor-pointer border rounded p-2 ${agreement.social_platforms.includes(p.k) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                    <input type="checkbox" checked={agreement.social_platforms.includes(p.k)} onChange={() => togglePlatform(p.k)} data-testid={`plat-${p.k}`} />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
              <h4 className="font-semibold mb-2 text-sm">Monthly Deliverables</h4>
              <div className="space-y-2">
                {agreement.social_platforms.map((pk) => {
                  const p = PLATFORMS.find((x) => x.k === pk) || { k: pk, label: pk };
                  return (
                    <div key={pk} className="border rounded-lg p-3 bg-slate-50/40">
                      <div className="text-sm font-semibold mb-2">{p.label}</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {CONTENT_TYPES.map((ct) => (
                          <div key={ct} className="flex items-center gap-1">
                            <Label className="text-xs w-16 capitalize">{ct}</Label>
                            <Input type="number" min="0" className="h-8 text-sm w-20"
                              value={(agreement.monthly_deliverables[pk] || {})[ct] || ''}
                              onChange={(e) => setDeliverable(pk, ct, e.target.value)}
                              data-testid={`deliv-${pk}-${ct}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {agreement.services.includes('seo') && (
            <Card className="p-4">
              <h3 className="font-semibold mb-2 text-emerald-800">SEO Setup</h3>
              <Label className="text-xs">Target Keywords (comma separated)</Label>
              <Input value={(agreement.target_keywords || []).join(', ')}
                onChange={(e) => setAgreement({ ...agreement, target_keywords: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                placeholder="Apartments in Chennai, Luxury Villas, Best Builder Chennai"
                data-testid="keywords-input" />
              <Label className="text-xs mt-2">Monthly Backlink Target</Label>
              <Input type="number" min="0" className="max-w-[120px]"
                value={agreement.monthly_backlink_target || 0}
                onChange={(e) => setAgreement({ ...agreement, monthly_backlink_target: parseInt(e.target.value, 10) || 0 })}
                data-testid="backlink-target" />
            </Card>
          )}

          <Card className="p-4">
            <Label className="text-xs font-semibold">Internal Notes</Label>
            <textarea rows={3} className="w-full border rounded px-2 py-1.5 text-sm"
              value={agreement.notes || ''}
              onChange={(e) => setAgreement({ ...agreement, notes: e.target.value })} />
          </Card>
        </>
      )}
    </div>
  );
}

// ═════════════════════ Social Roster Tab ═════════════════════

function RosterTab({ clients, users, clientLabel, userLabel }) {
  const [activeClient, setActiveClient] = useState('');
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [posts, setPosts] = useState([]);
  const [counters, setCounters] = useState({ rows: [], total_posts: 0, total_posted: 0 });
  const [activeDay, setActiveDay] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!activeClient) { setPosts([]); setCounters({ rows: [], total_posts: 0, total_posted: 0 }); return; }
    try {
      const [pr, cr] = await Promise.all([
        axios.get(`${API}/api/dmo/social-posts?client_id=${activeClient}&month=${month}`, opts),
        axios.get(`${API}/api/dmo/social-posts/counters?client_id=${activeClient}&month=${month}`, opts),
      ]);
      setPosts(pr.data || []);
      setCounters(cr.data || { rows: [], total_posts: 0, total_posted: 0 });
    } catch (e) { toast.error('Load failed: ' + (e.response?.data?.detail || e.message)); }
  }, [activeClient, month]);
  useEffect(() => { load(); }, [load]);

  const monthDate = useMemo(() => new Date(`${month}-01T00:00:00`), [month]);
  const monthLabel = monthDate.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  const cells = useMemo(() => {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const out = [];
    for (let i = 0; i < first.getDay(); i++) out.push(null);
    for (let d = 1; d <= last.getDate(); d++) out.push(toIsoDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), d)));
    return out;
  }, [monthDate]);
  const postsByDay = useMemo(() => {
    const m = {};
    posts.forEach((p) => { (m[p.date] = m[p.date] || []).push(p); });
    return m;
  }, [posts]);
  const dayPosts = activeDay ? (postsByDay[activeDay] || []) : [];

  const openEditor = (dateIso, existing = null) => {
    if (!activeClient) { toast.error('Pick a client first'); return; }
    setEditing(existing || {
      client_id: activeClient, date: dateIso, month: dateIso.slice(0, 7),
      platform: 'facebook', content_type: 'poster', title: '', caption: '', campaign: '', hashtags: '',
      assigned_designer: '', assigned_content_writer: '', assigned_video_editor: '', assigned_sm_executive: '',
      status: 'planned', assets: [],
    });
    setEditorOpen(true);
  };
  const savePost = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await axios.put(`${API}/api/dmo/social-posts/${editing.id}`, editing, opts);
      } else {
        await axios.post(`${API}/api/dmo/social-posts`, editing, opts);
      }
      toast.success('Post saved');
      setEditorOpen(false);
      load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await axios.delete(`${API}/api/dmo/social-posts/${id}`, opts);
    load();
  };

  const activeClients = clients.filter((c) => !!c.id);
  const activeUsers = users.filter((u) => !!u.id);
  const today = toIsoDate(new Date());

  return (
    <div className="space-y-3">
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <Label className="text-xs font-semibold">Client:</Label>
        <div className="min-w-[240px]">
          <SearchableSelect
            testId="roster-client"
            value={activeClient}
            onChange={setActiveClient}
            placeholder="Pick a client (type to search)…"
            options={[
              { value: '', label: '— Pick a client —' },
              ...activeClients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
            ]}
          />
        </div>
        <Label className="text-xs font-semibold">Month:</Label>
        <input type="month" className="text-sm border rounded px-3 py-2 bg-white"
          value={month} onChange={(e) => setMonth(e.target.value)} data-testid="roster-month" />
        {activeClient && (
          <div className="ml-auto text-xs text-gray-600">
            <strong>{counters.total_posts}</strong> planned · <strong className="text-emerald-700">{counters.total_posted}</strong> posted
          </div>
        )}
      </Card>

      {!activeClient ? (
        <Card className="p-8 text-center text-sm text-gray-400">Pick a client to view / plan their social media roster.</Card>
      ) : (
        <>
          {/* Deliverable counters */}
          {counters.rows.length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3 text-emerald-800">Monthly Deliverable Progress</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {counters.rows.map((r, i) => {
                  const capped = Math.min(100, r.pct);
                  return (
                    <div key={i} className="border rounded p-2" data-testid={`counter-${r.platform}-${r.content_type}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <div className="font-semibold uppercase">{r.platform} · {r.content_type}</div>
                        <div>
                          <span className="text-gray-700">{r.created}/{r.required || '∞'}</span>
                          {r.over > 0 && <span className="ml-1 text-[10px] px-1 rounded bg-emerald-500 text-white font-bold">+{r.over} over</span>}
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded overflow-hidden">
                        <div className={`h-full transition-all ${capped >= 100 ? 'bg-emerald-500' : capped >= 60 ? 'bg-blue-500' : 'bg-amber-500'}`}
                          style={{ width: `${capped}%` }} />
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {r.remaining > 0 ? `${r.remaining} remaining` : '✓ target met'} · {r.posted} posted
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Calendar grid */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold">{monthLabel}</div>
              <Button size="sm" onClick={() => openEditor(today)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="roster-new-post">
                + New Post
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider mb-1 text-gray-500">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((iso, i) => {
                if (!iso) return <div key={i} />;
                const list = postsByDay[iso] || [];
                const isToday = iso === today;
                const isActive = iso === activeDay;
                return (
                  <button
                    key={i}
                    onClick={() => setActiveDay(iso === activeDay ? '' : iso)}
                    onDoubleClick={() => openEditor(iso)}
                    className={`text-left rounded p-1.5 min-h-[80px] border transition-colors ${isActive ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : isToday ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`roster-day-${iso}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className={`text-[11px] font-bold ${isToday ? 'text-amber-700' : 'text-slate-700'}`}>{Number(iso.slice(-2))}</div>
                      {list.length > 0 && <span className="text-[9px] font-bold px-1 rounded bg-slate-700 text-white">{list.length}</span>}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {list.slice(0, 3).map((p) => (
                        <div key={p.id} className={`text-[9px] truncate rounded px-1 py-0.5 ${POST_STATUS_COLOR[p.status] || ''}`}
                          title={`${p.platform} · ${p.content_type} · ${p.title || p.caption}`}>
                          {p.platform.slice(0, 2).toUpperCase()} · {p.title || p.content_type}
                        </div>
                      ))}
                      {list.length > 3 && <div className="text-[9px] text-gray-500">+{list.length - 3} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Day detail */}
          {activeDay && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-emerald-800">
                  {new Date(activeDay).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  <span className="text-xs font-normal text-gray-500 ml-2">({dayPosts.length})</span>
                </h3>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => openEditor(activeDay)} data-testid="day-add-post">+ Post</Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveDay('')}>Close</Button>
                </div>
              </div>
              {dayPosts.length === 0 ? (
                <div className="text-sm text-gray-400 py-4 text-center">No posts planned for this day.</div>
              ) : (
                <ul className="space-y-1.5">
                  {dayPosts.map((p) => (
                    <li key={p.id} className={`flex items-center gap-3 p-2 rounded border ${POST_STATUS_COLOR[p.status]?.split(' ')[0] || 'bg-white'} border-gray-200 hover:shadow`} data-testid={`day-post-${p.id}`}>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-white uppercase">{p.platform}</span>
                      <span className="text-[10px] uppercase text-gray-600">{p.content_type}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.title || p.caption || '(no title)'}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {p.campaign && `📢 ${p.campaign} · `}
                          {p.assigned_designer && `🎨 ${userLabel[p.assigned_designer] || 'designer'} · `}
                          {p.assigned_content_writer && `✍ ${userLabel[p.assigned_content_writer] || 'writer'} · `}
                          {p.assigned_video_editor && `🎬 ${userLabel[p.assigned_video_editor] || 'editor'} · `}
                          {p.assigned_sm_executive && `📱 ${userLabel[p.assigned_sm_executive] || 'sm exec'}`}
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${POST_STATUS_COLOR[p.status] || ''}`}>{p.status.replace(/_/g, ' ')}</span>
                      <Button size="sm" variant="outline" onClick={() => openEditor(p.date, p)}>Edit</Button>
                      <button onClick={() => deletePost(p.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete">🗑</button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </>
      )}

      {/* Post Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl" data-testid="post-editor">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit Post' : 'New Post'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Date *</Label>
                  <Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
                <div><Label className="text-xs">Platform *</Label>
                  <select className="w-full border rounded px-2 py-2 text-sm bg-white" value={editing.platform}
                    onChange={(e) => setEditing({ ...editing, platform: e.target.value })}>
                    {PLATFORMS.map((p) => <option key={p.k} value={p.k}>{p.label}</option>)}
                  </select></div>
                <div><Label className="text-xs">Content Type *</Label>
                  <select className="w-full border rounded px-2 py-2 text-sm bg-white" value={editing.content_type}
                    onChange={(e) => setEditing({ ...editing, content_type: e.target.value })}>
                    {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
              </div>
              <div><Label className="text-xs">Title / Creative Name</Label>
                <Input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Independence Day Sale banner" data-testid="post-title" /></div>
              <div><Label className="text-xs">Caption</Label>
                <textarea rows={3} className="w-full border rounded px-2 py-1.5 text-sm"
                  value={editing.caption || ''} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} data-testid="post-caption" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Campaign</Label>
                  <Input value={editing.campaign || ''} onChange={(e) => setEditing({ ...editing, campaign: e.target.value })} placeholder="e.g. Diwali 2026" /></div>
                <div><Label className="text-xs">Hashtags</Label>
                  <Input value={editing.hashtags || ''} onChange={(e) => setEditing({ ...editing, hashtags: e.target.value })} placeholder="#chennai #realestate" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">🎨 Designer</Label>
                  <SearchableSelect
                    value={editing.assigned_designer || ''}
                    onChange={(v) => setEditing({ ...editing, assigned_designer: v })}
                    placeholder="Pick designer…"
                    options={[{ value: '', label: '—' }, ...activeUsers.map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email }))]}
                  /></div>
                <div><Label className="text-xs">✍ Content Writer</Label>
                  <SearchableSelect
                    value={editing.assigned_content_writer || ''}
                    onChange={(v) => setEditing({ ...editing, assigned_content_writer: v })}
                    placeholder="Pick writer…"
                    options={[{ value: '', label: '—' }, ...activeUsers.map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email }))]}
                  /></div>
                <div><Label className="text-xs">🎬 Video Editor</Label>
                  <SearchableSelect
                    value={editing.assigned_video_editor || ''}
                    onChange={(v) => setEditing({ ...editing, assigned_video_editor: v })}
                    placeholder="Pick editor…"
                    options={[{ value: '', label: '—' }, ...activeUsers.map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email }))]}
                  /></div>
                <div><Label className="text-xs">📱 SM Executive</Label>
                  <SearchableSelect
                    value={editing.assigned_sm_executive || ''}
                    onChange={(v) => setEditing({ ...editing, assigned_sm_executive: v })}
                    placeholder="Pick SM exec…"
                    options={[{ value: '', label: '—' }, ...activeUsers.map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email }))]}
                  /></div>
              </div>
              <div><Label className="text-xs">Status</Label>
                <select className="w-full border rounded px-2 py-2 text-sm bg-white" value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="post-status">
                  {POST_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
                <Button onClick={savePost} data-testid="save-post" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {editing.id ? 'Update Post' : 'Create Post'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═════════════════════ SEO Off-Page Tab ═════════════════════

function SeoTab({ clients, users, clientLabel, userLabel }) {
  const [rows, setRows] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterClient !== 'all') params.set('client_id', filterClient);
    if (filterStatus !== 'all') params.set('status', filterStatus);
    try {
      const r = await axios.get(`${API}/api/dmo/seo-submissions?${params.toString()}`, opts);
      setRows(r.data || []);
    } catch (e) { toast.error('Load failed: ' + (e.response?.data?.detail || e.message)); }
  }, [filterClient, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing({
      client_id: filterClient !== 'all' ? filterClient : '',
      date: toIsoDate(new Date()),
      keyword: '', submission_type: 'backlink', submission_url: '',
      website_name: '', domain_authority: 0, anchor_text: '', remarks: '',
      status: 'pending', assigned_to: '',
    });
    setEditorOpen(true);
  };
  const save = async () => {
    if (!editing?.client_id) { toast.error('Client is required'); return; }
    try {
      if (editing.id) await axios.put(`${API}/api/dmo/seo-submissions/${editing.id}`, editing, opts);
      else await axios.post(`${API}/api/dmo/seo-submissions`, editing, opts);
      toast.success('SEO entry saved');
      setEditorOpen(false); load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this SEO entry?')) return;
    await axios.delete(`${API}/api/dmo/seo-submissions/${id}`, opts);
    load();
  };
  const verify = async (id) => { await axios.post(`${API}/api/dmo/seo-submissions/${id}/verify`, {}, opts); toast.success('Verified'); load(); };
  const reject = async (id) => {
    const reason = window.prompt('Rejection reason?');
    if (reason === null) return;
    await axios.post(`${API}/api/dmo/seo-submissions/${id}/reject`, { reason }, opts);
    toast.success('Rejected'); load();
  };
  const needsCorr = async (id) => {
    const reason = window.prompt('What needs correction?');
    if (reason === null) return;
    await axios.post(`${API}/api/dmo/seo-submissions/${id}/needs-correction`, { reason }, opts);
    load();
  };

  const activeClients = clients.filter((c) => !!c.id);

  return (
    <div className="space-y-3">
      <Card className="p-3 flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Client:</Label>
        <div className="min-w-[220px]">
          <SearchableSelect
            testId="seo-filter-client"
            value={filterClient}
            onChange={setFilterClient}
            placeholder="All Clients"
            options={[
              { value: 'all', label: 'All Clients' },
              ...activeClients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
            ]}
          />
        </div>
        <Label className="text-xs">Status:</Label>
        <select className="text-xs border rounded px-2 py-1 bg-white" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="seo-filter-status">
          <option value="all">All</option>
          {Object.keys(SEO_STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="seo-bulk-btn">Bulk Entry</Button>
          <Button size="sm" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="seo-new-btn">+ New Entry</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Client</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-left">Keyword</th>
              <th className="p-2 text-left">URL</th>
              <th className="p-2 text-left">DA</th>
              <th className="p-2 text-left">Assigned</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400 italic">No SEO entries — click + New Entry or Bulk Entry to start.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-slate-50/50" data-testid={`seo-row-${r.id}`}>
                <td className="p-2 text-xs">{r.date}</td>
                <td className="p-2 text-xs">{clientLabel[r.client_id] || r.client_id}</td>
                <td className="p-2 text-xs uppercase">{r.submission_type.replace(/_/g, ' ')}</td>
                <td className="p-2 text-xs">{r.keyword || '—'}</td>
                <td className="p-2 text-xs max-w-[240px] truncate"><a href={r.submission_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" title={r.submission_url}>{r.website_name || r.submission_url || '—'}</a></td>
                <td className="p-2 text-xs">{r.domain_authority || '—'}</td>
                <td className="p-2 text-xs">{userLabel[r.assigned_to] || '—'}</td>
                <td className="p-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${SEO_STATUS_COLOR[r.status] || ''}`}>{r.status.replace(/_/g, ' ')}</span>
                  {r.rejection_reason && <div className="text-[10px] text-rose-600 mt-0.5" title={r.rejection_reason}>{r.rejection_reason.slice(0, 30)}</div>}
                </td>
                <td className="p-2 text-xs flex items-center gap-1">
                  {r.status !== 'verified' && <button onClick={() => verify(r.id)} className="text-emerald-600 hover:text-emerald-800" title="Verify" data-testid={`seo-verify-${r.id}`}>✓</button>}
                  {r.status !== 'rejected' && <button onClick={() => reject(r.id)} className="text-rose-600 hover:text-rose-800" title="Reject" data-testid={`seo-reject-${r.id}`}>✗</button>}
                  <button onClick={() => needsCorr(r.id)} className="text-amber-600 hover:text-amber-800" title="Needs correction">⟳</button>
                  <button onClick={() => { setEditing(r); setEditorOpen(true); }} className="text-blue-600 hover:text-blue-800" title="Edit">✎</button>
                  <button onClick={() => del(r.id)} className="text-rose-500 hover:text-rose-700" title="Delete">🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Single Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg" data-testid="seo-editor">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit SEO Entry' : 'New SEO Entry'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Client *</Label>
                  <SearchableSelect
                    testId="seo-client"
                    value={editing.client_id}
                    onChange={(v) => setEditing({ ...editing, client_id: v })}
                    placeholder="Pick a client…"
                    options={[
                      { value: '', label: '—' },
                      ...activeClients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
                    ]}
                  /></div>
                <div><Label className="text-xs">Date</Label>
                  <Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Submission Type</Label>
                  <select className="w-full border rounded px-2 py-2 text-sm bg-white" value={editing.submission_type}
                    onChange={(e) => setEditing({ ...editing, submission_type: e.target.value })}>
                    {SEO_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select></div>
                <div><Label className="text-xs">Domain Authority</Label>
                  <Input type="number" min="0" max="100" value={editing.domain_authority || 0}
                    onChange={(e) => setEditing({ ...editing, domain_authority: parseInt(e.target.value, 10) || 0 })} /></div>
              </div>
              <div><Label className="text-xs">Target Keyword</Label>
                <Input value={editing.keyword || ''} onChange={(e) => setEditing({ ...editing, keyword: e.target.value })} /></div>
              <div><Label className="text-xs">Submission URL</Label>
                <Input value={editing.submission_url || ''} onChange={(e) => setEditing({ ...editing, submission_url: e.target.value })} placeholder="https://…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Website Name</Label>
                  <Input value={editing.website_name || ''} onChange={(e) => setEditing({ ...editing, website_name: e.target.value })} /></div>
                <div><Label className="text-xs">Anchor Text</Label>
                  <Input value={editing.anchor_text || ''} onChange={(e) => setEditing({ ...editing, anchor_text: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Remarks</Label>
                <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm"
                  value={editing.remarks || ''} onChange={(e) => setEditing({ ...editing, remarks: e.target.value })} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-seo">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Entry */}
      <BulkSeoDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        clients={activeClients}
        defaultClient={filterClient !== 'all' ? filterClient : ''}
        onSaved={() => { setBulkOpen(false); load(); }}
      />
    </div>
  );
}

function BulkSeoDialog({ open, onClose, clients, defaultClient, onSaved }) {
  const [client, setClient] = useState(defaultClient);
  const [date, setDate] = useState(toIsoDate(new Date()));
  const [submissionType, setSubmissionType] = useState('backlink');
  const [keyword, setKeyword] = useState('');
  const [textBlob, setTextBlob] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setClient(defaultClient); }, [defaultClient, open]);

  const submit = async () => {
    if (!client) { toast.error('Pick a client'); return; }
    const lines = textBlob.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error('Paste at least one URL'); return; }
    const rows = lines.map((line) => {
      // Format supported: "url" OR "url | website_name | DA" OR "url,website,DA"
      const parts = line.split(/\s*[|,]\s*/);
      const url = parts[0];
      const website = parts[1] || '';
      const da = parseInt(parts[2], 10) || 0;
      return {
        client_id: client, date, submission_type: submissionType, keyword,
        submission_url: url, website_name: website, domain_authority: da,
        status: 'pending',
      };
    });
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/dmo/seo-submissions/bulk`, rows, opts);
      toast.success(`Added ${r.data.inserted} entries`);
      setTextBlob('');
      onSaved();
    } catch (e) { toast.error('Bulk save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="seo-bulk-dialog">
        <DialogHeader><DialogTitle>Bulk SEO Entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Client *</Label>
              <SearchableSelect
                testId="bulk-client"
                value={client}
                onChange={setClient}
                placeholder="Pick a client…"
                options={[
                  { value: '', label: '—' },
                  ...clients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
                ]}
              /></div>
            <div><Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label className="text-xs">Submission Type</Label>
              <select className="w-full border rounded px-2 py-2 text-sm bg-white" value={submissionType}
                onChange={(e) => setSubmissionType(e.target.value)}>
                {SEO_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select></div>
            <div><Label className="text-xs">Target Keyword</Label>
              <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs">URLs — one per line</Label>
            <p className="text-[10px] text-gray-500 mb-1">Formats: <code>url</code>, or <code>url | website_name | DA</code></p>
            <textarea rows={8} className="w-full border rounded px-2 py-1.5 text-sm font-mono"
              placeholder="https://example.com/backlink1 | Example Site | 45&#10;https://another.com/profile | Another | 32"
              value={textBlob} onChange={(e) => setTextBlob(e.target.value)}
              data-testid="bulk-urls" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-bulk-seo">
              {saving ? 'Saving…' : `Save ${textBlob.split('\n').filter((l) => l.trim()).length} entries`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
